/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import Retracked from 'js/app/retracked';
import API from 'js/lib/api';

import { SvgArrowLeft } from '@coursera/coursera-ui/svg';

import Submit from 'bundles/authentication/components/Submit';
import { AUTHENTICATION_MODAL_MODES, EMAIL_CONSTRAINT } from 'bundles/authentication/constants';
import type { AUTHENTICATION_MODAL_MODES_KEY } from 'bundles/authentication/constants';
import EmailInput from 'bundles/authentication/shared/EmailInput';
import Form from 'bundles/authentication/shared/Form';
import LinkButton from 'bundles/authentication/shared/components/authentication-modal/LinkButton';
import withSingleTracked from 'bundles/common/components/withSingleTracked';

import _t from 'i18n!nls/authentication';

export type Props = {
  className?: string;
  navigate: (path: AUTHENTICATION_MODAL_MODES_KEY, options?: $TSFixMe) => void;
  magicLinkRedirect?: string;
};

const TrackedSubmit = withSingleTracked({ type: 'BUTTON' })(Submit);

const api = API('', { type: 'rest' });

const styles = {
  container: css`
    display: grid;
    grid-gap: var(--cds-spacing-300);
    grid-template-columns: 1fr;
    margin: 0 auto;
    max-width: 396px;
    padding: var(--cds-spacing-400) var(--cds-spacing-200);
  `,
  description: css`
    text-align: center;
  `,
  hidden: css`
    display: none;
  `,
  form: css`
    display: grid;
    grid-gap: var(--cds-spacing-150);
    grid-template-columns: 1fr;
  `,
  navigation: css`
    margin: var(--cds-spacing-150) auto 0;
    text-align: center;
    display: flex;
  `,
  navigationSent: css`
    margin-top: 150px;
  `,
  navigationIcon: css`
    margin-right: 0.6rem;
  `,
  section: css`
    &:nth-child(1) {
      -ms-grid-row: 1;
    }

    &:nth-child(2) {
      -ms-grid-row: 2;
    }

    &:nth-child(3) {
      -ms-grid-row: 3;
    }

    &:nth-child(4) {
      -ms-grid-row: 4;
    }
  `,
  title: css`
    color: #1f1f1f;
    font-family: inherit;
    font-size: 24px;
    line-height: 30px;
    margin: 0;
    text-align: center;

    @media (min-width: 600px) {
      font-size: 34px;
      line-height: 42px;
    }
  `,
  submit: css`
    margin-top: var(--cds-spacing-150);
  `,
  separator: css`
    margin: var(--cds-spacing-50) 0;
  `,
};

class RequestMagicLink extends React.Component<Props> {
  navigateToLogin = () => {
    const { navigate } = this.props;

    navigate(AUTHENTICATION_MODAL_MODES.LOGIN);
  };

  sendMagicLink = ({ email }: { email: string }) => {
    const { magicLinkRedirect } = this.props;

    api.post('/api/magicLinks.v1/encode', { data: { email, redirectUrl: magicLinkRedirect } });

    this.navigateToMagicLink(email);
  };

  navigateToMagicLink = (email: string) => {
    const { navigate } = this.props;

    navigate(AUTHENTICATION_MODAL_MODES.MAGICLINK, { email });
  };

  render() {
    const { className } = this.props;

    return (
      <section className={className} css={styles.container} aria-live="polite">
        <header css={styles.section}>
          <h1 css={styles.title}>{_t('Sign in with email')}</h1>
        </header>
        <p css={[styles.description, styles.section]}>
          {_t('Enter the email address associated with your account, and we’ll send a login link to your inbox.')}
        </p>
        <Form
          css={[styles.form, styles.section]}
          constraints={{ email: EMAIL_CONSTRAINT }}
          onSubmit={(values) => this.sendMagicLink(values as { email: string })}
        >
          <EmailInput css={[styles.section]} />
          <TrackedSubmit css={[styles.submit, styles.section]} trackingName="send_login_link_button">
            {_t('Email me a login link')}
          </TrackedSubmit>
        </Form>
        <p css={[styles.navigation, styles.section]}>
          <LinkButton css={styles.navigation} onClick={this.navigateToLogin}>
            <div css={styles.navigationIcon}>
              <SvgArrowLeft color="#2A73CC" size={15} />
            </div>
            {_t('Back to Log in')}
          </LinkButton>
        </p>
      </section>
    );
  }
}

export default Retracked.createTrackedContainer<Props>(() => ({
  namespace: {
    page: 'magic_link',
  },
}))(RequestMagicLink);
